<template>
	<div class="pd-medium">
		<h2 class="mn-b-medium">Overview</h2>

		<!-- Stats Cards -->
		<div class="mn-b-medium cols-3 mobile:cols-1 gap-small">
			<router-link
				v-if="isModuleInstalled('organizations')"
				:to="`/users/${auth.state.user._id}/organizations`"
				class="d-block bg-card pd-medium radius-medium hover-scale-1 transition-default"
			>
				<div class="flex flex-v-center gap-small mn-b-small">
					<div class="radius-small bg-accent flex flex-center">
						<Icon name="groups" class="i-medium mn-small" :color="'rgb(var(--text-inverse))'"/>
					</div>
					<span class="t-secondary t-small">My Groups</span>
				</div>
				<p class="h2 fw-semi">{{ stats.organizations || 0 }}</p>
			</router-link>

			<router-link
				v-if="isModuleInstalled('orders')"
				:to="`/users/${auth.state.user._id}/orders`"
				class="d-block bg-card pd-medium radius-medium hover-scale-1 transition-default"
			>
				<div class="flex flex-v-center gap-small mn-b-small">
					<div class="radius-small bg-accent flex flex-center">
						<Icon name="orders" class="i-medium mn-small" :color="'rgb(var(--text-inverse))'"/>
					</div>
					<span class="t-secondary t-small">My Orders</span>
				</div>
				<p class="h2 fw-semi">{{ stats.orders || 0 }}</p>
			</router-link>

			<router-link
				v-if="isModuleInstalled('events')"
				:to="`/users/${auth.state.user._id}/events`"
				class="d-block bg-card pd-medium radius-medium hover-scale-1 transition-default"
			>
				<div class="flex flex-v-center gap-small mn-b-small">
					<div class="radius-small bg-accent flex flex-center">
						<Icon name="events" class="i-medium mn-small" :color="'rgb(var(--text-inverse))'"/>
					</div>
					<span class="t-secondary t-small">My Events</span>
				</div>
				<p class="h2 fw-semi">{{ stats.events || 0 }}</p>
			</router-link>
		</div>

		<!-- Content Grid -->
		<div v-if="isModuleInstalled('orders') || isModuleInstalled('events')" class="cols-2 mobile:cols-1 gap-small">
			<!-- Recent Orders -->
			<Block v-if="isModuleInstalled('orders')" title="Recent Orders">
				<Feed
					:showLoadMore="false"
					:states="{
						empty: {
							title: 'No Orders',
							description: 'You have no orders yet.'
						}
					}"
					:store="{
						read: (options) => orders.actions.read(options),
						state: orders.state
					}"
					:options="{
						customer: auth.state.user._id,
						limit: 5,
						page: 1
					}"
					v-slot="{ items }"
				>
					<Table
						class="radius-small bg-base"
						:items="items"
						:columns="orderColumns"
					>
						<template #cell-_id="{ value }">
							<span class="t-secondary fw-medium">#{{ formatId(value) }}</span>
						</template>
						<template #cell-positions="{ value }">
							<span class="t-secondary">{{ value?.length || 0 }} items</span>
						</template>
						<template #cell-status="{ value }">
							<span
								class="pd-thin pd-l-small pd-r-small radius-small t-small fw-medium"
								:class="getStatusClass(value)"
							>
								{{ value }}
							</span>
						</template>
						<template #cell-createdAt="{ value }">
							<span class="t-secondary t-small">{{ formatDate(value, { dateOnly: true }) }}</span>
						</template>
					</Table>
				</Feed>
			</Block>

			<!-- Upcoming Events -->
			<Block v-if="isModuleInstalled('events')" title="Upcoming Events">
				<Feed
					:showLoadMore="false"
					:states="{
						empty: {
							title: 'No Events',
							description: 'You have no upcoming events.'
						}
					}"
					:store="{
						read: (options) => events.read(options)
					}"
					:options="{
						creator: auth.state.user._id,
						sortParam: 'date.start',
						sortOrder: 'asc',
						limit: 5,
						page: 1
					}"
					v-slot="{ items }"
					class="flex flex-column gap-thin"
				>
					<Card
						v-for="event in items"
						:key="event._id"
						:to="`/events/${event.url || event._id}`"
						:photo="event.cover"
						placeholder="events"
						:title="event.name"
						:subtitle="formatDate(event.date?.start, { dateOnly: true })"
						classImage="flex-none w-3r h-3r radius-small bg-accent-transp-10 flex flex-center o-hidden"
						classContent="flex-child o-hidden"
						class="bg-base pd-small radius-small hover-scale-1 transition-default flex flex-v-center gap-small"
					>
						<template #actions>
							<span
								class="flex-none pd-thin pd-l-small pd-r-small radius-small t-small fw-medium"
								:class="getEventStatusClass(event.date)"
							>
								{{ getEventStatus(event.date) }}
							</span>
						</template>
					</Card>
				</Feed>
			</Block>
		</div>
	</div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

import Block from '@martyrs/src/design-system/components/Block/Block.vue'
import Feed from '@martyrs/src/design-system/components/Feed/Feed.vue'
import Table from '@martyrs/src/design-system/components/Table/Table.vue'
import Card from '@martyrs/src/modules/core/views/components/blocks/Card.vue'

import Icon from '@martyrs/src/design-system/components/Icon/Icon.vue'
import { useGlobalMixins } from '@martyrs/src/modules/core/views/mixins/mixins.js'
import { auth } from '@martyrs/src/modules/auth/views/store/auth.js'
import { orders } from '@martyrs/src/modules/orders/store/orders.js'
import { events } from '@martyrs/src/modules/events/store/events.js'

const { formatDate, isModuleInstalled } = useGlobalMixins()

const stats = ref({
	organizations: 0,
	orders: 0,
	events: 0
})

const orderColumns = [
	{ key: '_id', label: 'Order' },
	{ key: 'positions', label: 'Items' },
	{ key: 'status', label: 'Status' },
	{ key: 'createdAt', label: 'Date' }
]

const formatId = (id) => id ? `${id.slice(0, 4)}...${id.slice(-4)}` : ''

const getStatusClass = (status) => {
	const classes = {
		pending: 'bg-accent-secondary-transp-10 t-accent-secondary',
		processing: 'bg-accent-transp-10 t-accent',
		completed: 'bg-positive-transp-10 t-positive',
		canceled: 'bg-negative-transp-10 t-negative'
	}
	return classes[status] || 'bg-card t-secondary'
}

const getEventStatus = (date) => {
	if (!date?.start) return 'No date'
	const now = new Date()
	const start = new Date(date.start)
	const end = date.end ? new Date(date.end) : start
	if (now > end) return 'Finished'
	if (now >= start && now <= end) return 'Live'
	const diffDays = Math.ceil((start - now) / (1000 * 60 * 60 * 24))
	if (diffDays <= 1) return 'Tomorrow'
	if (diffDays <= 7) return `In ${diffDays} days`
	return 'Upcoming'
}

const getEventStatusClass = (date) => {
	const status = getEventStatus(date)
	if (status === 'Finished') return 'bg-card t-secondary'
	if (status === 'Live') return 'bg-positive-transp-10 t-positive'
	if (status === 'Tomorrow') return 'bg-accent-secondary-transp-10 t-accent-secondary'
	return 'bg-accent-transp-10 t-accent'
}

onMounted(async () => {
	// Load stats
	try {
		const [orgsData, ordersData, eventsData] = await Promise.all([
			// Load counts if needed
		])
	} catch (e) {
		console.error('Failed to load dashboard stats:', e)
	}
})
</script>

<style lang="scss">
</style>
